import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import moment from "moment";

import { IGetAgency } from "../../../../services/agency.service";
import {
    IEmployeeCalendarResponse,
    IGetCaseManagerResponse,
    ICaseManagerOption,
    ICaseManagerSearchPayload,
    IEmployeeFilterList,
    UserService
} from "../../../../services/user-service";
import { ApplicationEnum, ApplicationNameEnum } from "../../../../enums/ApplicationEnum";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../enums/PermissionActionEnum";
import { EmployeesScheduledFilter } from "../../../../enums/EmployeesScheduledFilterEnum";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { CalendarViewTypeEnum } from "../../../../enums/CalendarViewTypeEnum";
import { ITypeaheadOptions } from "../../../../resources-vue/vue-interfaces/i-typeahead";
import { IEnumResponse } from "../../../../resources-vue/vue-interfaces/i-enum";
import { IMultiSelectDropDownOption } from "../../../../resources-vue/vue-interfaces/i-grouped-multiselect";
import { adjustDate } from "../../../../common/adjust-date";
import { EnumsService } from "../../../../services/enums.service";
import eventBus from "../../../../utilities/eventBus";
import config from "../../../../common/config";
import { PatientsScheduledFilter } from "../../../../enums/PatientsScheduledFilterEnum";
import { useApp } from "../../../App/useApp";
import { helperTestingAgency } from "../../../../common/vue-helpers/testing-agency-check";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";

import WeeklyDateNavigation from "../WeeklyDateNavigation/WeeklyDateNavigation.vue";
import MultiSelect from "../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import MultiSelectFilter from "../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import TypeaheadInput from "../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import BranchDefaultFilter from "../../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import DailyDateNavigation from "../../../../resources-vue/vue-custom-elements/DailyDateNavigation/DailyDateNavigation.vue";

export default defineComponent({
    components: {
        MultiSelect,
        MultiSelectFilter,
        TypeaheadInput,
        DailyDateNavigation,
        WeeklyDateNavigation,
        BranchDefaultFilter
    },

    props: {
        isEmployeeCalendar: {
            type: Boolean,
            default: false
        },
        isPatientCalendar: {
            type: Boolean,
            default: false
        },
        fetchUpdatedCount: { type: Number, default: 0 },
        startDate: {
            type: Date
        },
        endDate: {
            type: Date
        },
        dailyDate: {
            type: Date
        },
        updateDateRange: {
            type: Function
        },
        employeeRefresh: {
            type: Function
        },
        employeesList: { type: Object, default: {} },
        patientsList: { type: Object, default: {} },
        listView: { type: String },
        dailyDateUpdate: {
            type: Function
        },
        handleViewChange: { type: Function },
        patientRefresh: {
            type: Function
        },
        showMainHeader: {
            type: Boolean
        }
    },

    data(vm) {
        return {
            _analyticsService: null as AnalyticsService,
            _userService: null as UserService,
            _enumsService: null as EnumsService,
            _permissionManager: null as PermissionManager,
            term: "" as string,
            selectedWeekdayRange: null as number,
            organizationId: "",
            scheduleFilter: 0,
            searchTimer: null,
            isWeekRangeChanged: false,
            selectedPatientStatus: null,
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            allAgencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            selectedBranches: [],
            agencyList: [] as IGetAgency[],
            ScheduledFilterEnums: EmployeesScheduledFilter,
            PatientScheduledFilterEnums: PatientsScheduledFilter,
            credentialsList: [] as string[],
            caseManagerList: [] as string[],
            titlesList: [] as string[],
            selectedCredentials: [] as ITypeaheadOptions[],
            selectedCaseManagers: [] as ICaseManagerOption[],
            selectedTitles: [] as ITypeaheadOptions[],
            weekDayRangeEnums: [] as IEnumResponse[],
            patientStatusEnums: [] as IEnumResponse[],
            selectedEmployees: [],
            selectedEmployeesSearch: [] as IEmployeeFilterList[],
            CalendarViewTypeEnum,
            selectedPatients: [],
            allUsers: [] as IEmployeeFilterList[],
            selectedAgenciesName: [] as string[],
            enumToMap,
            isBranchSynced: true,
            hasTestAgency: false,
            isPrimaryLocationFilter: false,
            suppressBranchRefresh: false,
            branchRefreshTimer: null
        };
    },

    async created() {
        this._enumsService = Container.instance.get(EnumsService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._userService = Container.instance.get(UserService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    async mounted() {
        let weekDayRangeEnums = await this._enumsService.getWeekDayRange();
        this.weekDayRangeEnums = weekDayRangeEnums.getJson();
        this.credentialsList = await this._userService.getUserCredentials();
        this.titlesList = await this._userService.getUserTitles();
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            const agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        if (this.isPatientCalendar) {
            let patientStatusEnums = await this._enumsService.getPatientStatus();
            this.patientStatusEnums = patientStatusEnums.getJson();
            this.selectedPatientStatus = patientStatusEnums.getEnumValue("active") as number;
        }
        this.selectedWeekdayRange = Number(this.weekDayRangeEnums[0].value);
        await this.setAgencyMultiSelect();
        if (this.$route.query.agencies) {
            const agencies = (this.$route.query.agencies as string).split(",");
            this.updateBranches(agencies);
        }
        if (this.$route.query.credentials) {
            const credentials = (this.$route.query.credentials as string).split(",");
            const selectedCredentials = credentials.map((cred: any) => {
                return {
                    name: cred.split("_")[0],
                    value: cred.split("_")[1]
                };
            });
            this.selectedItemsUpdated(selectedCredentials);
        }
        if (this.$route.query.titles) {
            const titles = (this.$route.query.titles as string).split(",");
            const selectedTitles = titles.map((title: any) => {
                return {
                    name: title.split("_")[0],
                    value: title.split("_")[1]
                };
            });
            this.selectedTitlesUpdated(selectedTitles);
        }

        if (this.$route.query.schedule) {
            const schedule = Number(this.$route.query.schedule);
            this.scheduleFilter = schedule;
        }

        if (this.$route.query.patientStatus && this.isPatientCalendar) {
            this.selectedPatientStatus = Number(this.$route.query.patientStatus);
        }
    },

    watch: {
        term: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                clearTimeout(this.searchTimer);

                this.searchTimer = setTimeout(
                    async (term) => {
                        if (term?.length > 2 || term === "") {
                            await this.emitPatientRefresh();
                        }
                    },
                    500,
                    this.term
                );
            }
        },

        async scheduleFilter() {
            if (this.isEmployeeCalendar) {
                await this.emitEmployeeRefresh();
            } else {
                await this.emitPatientRefresh();
            }
        },

        selectedWeekdayRange: {
            handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.isWeekRangeChanged = true;
                    let today = new Date(Date.now());
                    let dayRequested = newValue - 1;
                    if (dayRequested < 0) {
                        dayRequested = 0;
                    }

                    let todayDay = today.getDay();
                    let offset = dayRequested - todayDay;
                    if (offset > 0) {
                        offset -= 7;
                    }
                    let updatedStartDate = adjustDate(today, offset);
                    let updatedEndDate = adjustDate(updatedStartDate, 6);
                    if (this.isEmployeeCalendar) {
                        this.emitEmployeeRefresh({ startDate: updatedStartDate, endDate: updatedEndDate });
                    } else {
                        this.emitPatientRefresh({ startDate: updatedStartDate, endDate: updatedEndDate });
                    }
                }
                this.isWeekRangeChanged = false;
            },
            deep: true
        },

        selectedPatientStatus: async function (newValue, oldValue) {
            if (newValue !== oldValue && this.startDate) {
                await this.emitPatientRefresh();
            }
        },
        listView(newValue: CalendarViewTypeEnum, oldValue: CalendarViewTypeEnum) {
            if (newValue === CalendarViewTypeEnum.SummaryView && newValue !== oldValue) {
                this._analyticsService.logEvent({
                    category: AnalyticsCategory.EmployeeSummary,
                    action: " "
                });
            }
        }
    },

    methods: {
        getPrimaryHomeHealthAgencyIds() {
            return ["ae3934e9-100f-41cd-8c23-c5c176033f12", "397cb4c-de20-48d6-940e-11d4fea48a88"];
        },

        updateAgencyOptionStates() {
            const primaryHomeHealthAgencyIds = this.getPrimaryHomeHealthAgencyIds();
            this.agencyMultiSelectOptions = {};
            Object.entries(this.allAgencyMultiSelectOptions).forEach(([groupName, agencies]: [string, any[]]) => {
                const visibleAgencies = this.isPrimaryLocationFilter
                    ? agencies.filter(
                          (agency) =>
                              agency.application !== ApplicationEnum.AgencyCore ||
                              primaryHomeHealthAgencyIds.includes(agency.value.split("_")[0])
                      )
                    : agencies;
                this.agencyMultiSelectOptions[groupName] = visibleAgencies;
            });
            Object.values(this.agencyMultiSelectOptions).forEach((agencies: any[]) => {
                agencies.forEach((agency) => {
                    agency.disabled = false;
                });
            });
        },

        filterUnsupportedHomeHealthAgencies() {
            if (this.isPrimaryLocationFilter || !this.isEmployeeCalendar) {
                return;
            }
            const primaryHomeHealthAgencyIds = this.getPrimaryHomeHealthAgencyIds();
            const filteredAgencies = this.selectedAgencies.filter((agencyId) => {
                const agency = this.agencyList.find((item) => item.id === agencyId);
                return (
                    agency?.application !== ApplicationEnum.AgencyCore ||
                    primaryHomeHealthAgencyIds.includes(agencyId.split("_")[0])
                );
            });
            this.selectedAgencies.splice(0, this.selectedAgencies.length, ...filteredAgencies);
        },

        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.allAgencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                const allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                const schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                const filterableProducts = this.agencyList.filter((agency) => {
                    const isAllowed = allowedProducts.includes(agency.application);
                    const agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    const appName = ApplicationNameEnum[agency.application];

                    if (!this.allAgencyMultiSelectOptions[appName]) {
                        this.allAgencyMultiSelectOptions[appName] = [];
                    }

                    this.allAgencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id,
                        application: agency.application
                    });
                });

                this.updateAgencyOptionStates();

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    const values = agency.map((item: any) => item.value);
                    this.selectedAgencies.push(...values);
                });
                this.selectedAgenciesName = this.agencyList
                    .filter((agency) => this.selectedAgencies.includes(agency.id))
                    .map((agency) => agency.name);
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        selectEmployeeFetch(filter: string) {
            let employees = [];
            for (const [key, value] of Object.entries(this.employeesList)) {
                employees.push({ name: key, value: value });
            }
            return employees.filter(
                (emp) => !filter || emp.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
        },

        async fetchEmployeeList(filter: string, pageNumber: number = 1) {
            if (this.selectedAgencies.length > 0) {
                const employeeFilterOptions = {
                    page: pageNumber,
                    pageLength: 15,
                    term: filter,
                    agencies: this.selectedAgencies,
                    credentials: this.selectedCredentials?.map((credential) => credential.name),
                    titles: this.selectedTitles?.map((title) => title.name),
                    scheduleFilter: this.scheduleFilter,
                    startDate: this.startDate,
                    endDate: this.endDate
                };
                let data = await this._userService.getUsersForCalendar(employeeFilterOptions);
                if ((filter && pageNumber > 1) || (!filter && pageNumber > 1)) {
                    data.Items.map((user: IEmployeeCalendarResponse) => {
                        this.allUsers.push({
                            name: `${user.LastName ? `${user.LastName}, ` : ""}${user.FirstName} `,
                            value: user.Id
                        });
                    });
                } else if (filter && pageNumber === 1) {
                    this.allUsers = [] as IEmployeeFilterList[];
                    data.Items.map((user: IEmployeeCalendarResponse) => {
                        this.allUsers.push({
                            name: `${user.LastName ? `${user.LastName}, ` : ""}${user.FirstName} `,
                            value: user.Id
                        });
                    });
                } else {
                    this.allUsers = [];
                    data.Items.map((user: IEmployeeCalendarResponse) => {
                        this.allUsers.push({
                            name: `${user.LastName ? `${user.LastName}, ` : ""}${user.FirstName} `,
                            value: user.Id
                        });
                    });
                }
                return this.allUsers;
            }
            return [];
        },

        selectedEmployeesSearchUpdated(options: IEmployeeFilterList[]) {
            this.selectedEmployeesSearch = options;
            this.emitEmployeeRefresh();
        },

        updateBranches(branches: any) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...branches);
            this.selectedAgenciesName = this.agencyList
                .filter((agency: any) => this.selectedAgencies.includes(agency.id))
                .map((agency: any) => agency.name);
            const queryAgencies = this.selectedAgencies.filter((_, index: number) => index < 12);
            const queryCredentials = this.$route.query.credentials;
            const queryTitles = this.$route.query.titles;
            const querySchedule = this.$route.query.schedule;
            const queryStatus = this.$route.query.status;
            const queryPatientStatus = this.$route.query.patientStatus;
            if (this.isEmployeeCalendar) {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies.join(","),
                        credentials: queryCredentials ? queryCredentials : "",
                        title: queryTitles ? queryTitles : "",
                        schedule: querySchedule ? querySchedule : "",
                        status: queryStatus ? queryStatus : ""
                    }
                });
            } else {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies.join(","),
                        schedule: querySchedule ? querySchedule : "",
                        patientStatus: queryPatientStatus ? queryPatientStatus : ""
                    }
                });
            }
        },

        async emitEmployeeRefresh(options?: any) {
            let startDate = null as Date;
            let endDate = null as Date;
            if (options) {
                startDate = options.startDate;
                endDate = options.endDate;
            } else if (
                this.listView === CalendarViewTypeEnum.Weekly ||
                this.listView === CalendarViewTypeEnum.SummaryView
            ) {
                startDate = this.startDate;
                endDate = this.endDate;
            } else if (this.listView === CalendarViewTypeEnum.Daily) {
                startDate = this.dailyDate;
                endDate = this.dailyDate;
            }
            this.employeeRefresh({
                term: "",
                agencies: this.selectedAgencies,
                locationIds: this.selectedBranches,
                credentials: this.selectedCredentials?.map((credential) => credential.name),
                titles: this.selectedTitles?.map((title) => title.name),
                scheduleFilter: this.scheduleFilter,
                startDate: startDate,
                endDate: endDate,
                userIds: this.selectedEmployeesSearch.map((user) => user.value),
                IsPrimaryLocationFilter: this.isPrimaryLocationFilter
            });
        },

        async emitPatientRefresh(options?: any) {
            let startDate = null as Date;
            let endDate = null as Date;
            if (options) {
                startDate = options.startDate;
                endDate = options.endDate;
            } else if (this.listView === CalendarViewTypeEnum.Weekly) {
                startDate = this.startDate;
                endDate = this.endDate;
            }

            this.patientRefresh(
                {
                    term: this.term,
                    agencies: this.selectedAgencies,
                    locationIds: this.selectedBranches,
                    patientStatus: this.selectedPatientStatus,
                    scheduleFilter: this.scheduleFilter,
                    startDate: startDate,
                    endDate: endDate,
                    CaseManagerIds: this.selectedCaseManagers?.flatMap((cm) => cm.caseManagerIds || []) || []
                },
                this.isWeekRangeChanged,
                this.selectedWeekdayRange
            );
        },

        userCredentialsFetch(filter: string) {
            return this.credentialsList
                .map((credential, index) => ({
                    name: credential,
                    value: index.toString()
                }))
                .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        },

        userTitlesFetch(filter: string) {
            return this.titlesList
                .map((title, index) => ({
                    name: title,
                    value: index.toString()
                }))
                .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        },

        async caseManagerFetch(filter: string) {
            if (!filter || filter.length < 3) {
                return [];
            }
            const payload: ICaseManagerSearchPayload = {
                Term: filter.trim(),
                StartDate: moment(this.startDate).format("MM/DD/YYYY"),
                EndDate: moment(this.endDate).format("MM/DD/YYYY"),
                LocationIds: this.selectedBranches,
                Agencies: this.selectedAgencies,
                ScheduleFilter: this.scheduleFilter,
                PatientStatus: this.selectedPatientStatus
            };
            const response = await this._userService.getCaseManager(payload);
            return response.map((user: IGetCaseManagerResponse) => {
                const caseManagerIds = user.CaseMagerIds.map((cm: any) => cm.Id || cm);
                return {
                    name: `${user.FirstName} ${user.LastName}`.trim(),
                    value: user.LoginId,
                    caseManagerIds: caseManagerIds
                };
            });
        },
        selectedCaseManagersUpdated(options: ICaseManagerOption[]) {
            this.selectedCaseManagers = options || [];
            this.emitPatientRefresh();
            const queryAgencies = this.$route.query.agencies;
            const queryTitles = this.$route.query.titles;
            const querySchedule = this.$route.query.schedule;
            const queryStatus = this.$route.query.status;
            const queryCredentials = this.$route.query.credentials;
            const allCaseManagerIds = this.selectedCaseManagers.flatMap((cm) => cm.caseManagerIds || []);
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    credentials: queryCredentials ? queryCredentials : "",
                    schedule: querySchedule ? querySchedule : "",
                    titles: queryTitles ? queryTitles : "",
                    status: queryStatus ? queryStatus : "",
                    caseManagerIds: allCaseManagerIds.length ? allCaseManagerIds.join(",") : ""
                }
            });
        },

        selectedTitlesUpdated(options: any) {
            this.selectedTitles = options;
            this.emitEmployeeRefresh();
            const queryAgencies = this.$route.query.agencies;
            const queryCredentials = this.$route.query.credentials;
            const querySchedule = this.$route.query.schedule;
            const queryStatus = this.$route.query.status;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    credentials: queryCredentials ? queryCredentials : "",
                    schedule: querySchedule ? querySchedule : "",
                    titles: this.selectedTitles?.map((title) => `${title.name}_${title.value}`).join(","),
                    status: queryStatus ? queryStatus : ""
                }
            });
        },

        selectPatientFetch(filter: string) {
            const patients = [];
            for (const [key, value] of Object.entries(this.patientsList)) {
                patients.push({ name: key, value: value });
            }
            return patients.filter(
                (emp) => !filter || emp.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
        },

        selectedPatientsUpdated(options: any) {
            this.selectedPatients = options;
            const patients = this.selectedPatients.map((patient) => patient.name);
            eventBus.emit("highlightPatientNameMarker", patients);
        },

        handleBranchesOnChange(branches: any) {
            this.selectedBranches.length = 0;
            this.selectedBranches.push(...branches);
            this.scheduleBranchRefresh();
        },

        scheduleBranchRefresh() {
            clearTimeout(this.branchRefreshTimer);
            this.branchRefreshTimer = setTimeout(() => {
                this.suppressBranchRefresh = false;
                if (this.isEmployeeCalendar) {
                    this.emitEmployeeRefresh();
                } else {
                    this.emitPatientRefresh();
                }
            }, 0);
        },

        handlePrimaryLocationFilterChange(value: boolean) {
            this.isPrimaryLocationFilter = value;
            this.suppressBranchRefresh = true;
            this.updateAgencyOptionStates();
            const primaryHomeHealthAgencyIds = this.getPrimaryHomeHealthAgencyIds();
            if (value) {
                const filteredAgencies = this.selectedAgencies.filter((agencyId) => {
                    const agency = this.agencyList.find((item) => item.id === agencyId);
                    return (
                        agency?.application !== ApplicationEnum.AgencyCore ||
                        primaryHomeHealthAgencyIds.includes(agencyId.split("_")[0])
                    );
                });
                this.selectedAgencies.splice(0, this.selectedAgencies.length, ...filteredAgencies);
                this.selectedAgenciesName = this.agencyList
                    .filter((agency) => this.selectedAgencies.includes(agency.id))
                    .map((agency) => agency.name);
            } else {
                const unsupportedHomeHealthAgencyIds = this.agencyList
                    .filter(
                        (agency) =>
                            agency.application === ApplicationEnum.AgencyCore &&
                            !primaryHomeHealthAgencyIds.includes(agency.id.split("_")[0])
                    )
                    .map((agency) => agency.id);
                this.selectedAgencies.push(
                    ...unsupportedHomeHealthAgencyIds.filter((agencyId) => !this.selectedAgencies.includes(agencyId))
                );
                this.selectedAgenciesName = this.agencyList
                    .filter((agency) => this.selectedAgencies.includes(agency.id))
                    .map((agency) => agency.name);
            }
            this.scheduleBranchRefresh();
        },

        selectedItemsUpdated(options: any) {
            this.selectedCredentials = options;
            this.emitEmployeeRefresh();
            const queryAgencies = this.$route.query.agencies;
            const queryTitles = this.$route.query.titles;
            const querySchedule = this.$route.query.schedule;
            const queryStatus = this.$route.query.status;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    credentials: this.selectedCredentials
                        ?.map((credential) => `${credential.name}_${credential.value}`)
                        .join(","),
                    titles: queryTitles ? queryTitles : "",
                    schedule: querySchedule ? querySchedule : "",
                    status: queryStatus ? queryStatus : ""
                }
            });
        },
        handleScheduleChange() {
            const queryAgencies = this.$route.query.agencies;
            const queryCredentials = this.$route.query.credentials;
            const queryTitles = this.$route.query.titles;
            const queryStatus = this.$route.query.status;
            const queryPatientStatus = this.$route.query.patientStatus;
            if (this.isEmployeeCalendar) {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies ? queryAgencies : "",
                        credentials: queryCredentials ? queryCredentials : "",
                        titles: queryTitles ? queryTitles : "",
                        schedule: this.scheduleFilter,
                        status: queryStatus ? queryStatus : ""
                    }
                });
            } else {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies ? queryAgencies : "",
                        schedule: this.scheduleFilter,
                        patientStatus: queryPatientStatus ? queryPatientStatus : ""
                    }
                });
            }
        },
        handlePatientStatusChange() {
            const queryAgencies = this.$route.query.agencies;
            const querySchedule = this.$route.query.schedule;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    schedule: querySchedule ? querySchedule : "",
                    patientStatus: this.selectedPatientStatus
                }
            });
        }
    }
});
